// Fleet-wide history endpoint wrapper. Closely mirrors
// `use-fleet-host-history.ts`: same query-string builder and the
// minute-rounded `keyTo` cache-key trick so a live re-render doesn't
// bust the cache. Adds `keepPreviousData` so switching range presets
// keeps the prior charts/feed visible instead of flashing empty.

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { FLEET_HISTORY_REFETCH_MS } from "./fleet-intervals";
import type { FleetRangeValue } from "./use-fleet-range";

/** One bucketed sample across the whole fleet. */
export interface FleetHistoryTrendBucket {
	t: string; // ISO timestamp, bucket start
	totalSessions: number;
	reachableHosts: number;
	memUsedGB: number;
	memTotalGB: number;
	loadAvg1m: number;
}

export type FleetHistoryEventType = "version" | "api" | "reachability";

export interface FleetHistoryEvent {
	hostname: string;
	type: FleetHistoryEventType;
	at: string;
	summary: string;
}

export interface FleetHistoryAllAggregates {
	peakConcurrent: number;
	peakConcurrentAt: string | null;
	hostCount: number;
}

export interface FleetHistoryAllResponse {
	success: true;
	retentionDays: number;
	windowFrom: string;
	windowTo: string;
	trends: FleetHistoryTrendBucket[];
	events: FleetHistoryEvent[];
	aggregates: FleetHistoryAllAggregates;
}

interface QueryParams {
	from?: string;
	to?: string;
	range?: string;
	bucket: "auto";
}

function buildQuery(range: FleetRangeValue): QueryParams {
	if (range.kind === "preset") {
		return { range: range.preset, bucket: "auto" };
	}
	return {
		from: range.from.toISOString(),
		to: range.to.toISOString(),
		bucket: "auto",
	};
}

/**
 * `to`-rounding key: when the range is "live" (preset ending now), bucket
 * the cache key to the nearest minute so the snapshot poller's tick
 * doesn't immediately refetch on every render.
 */
function keyTo(range: FleetRangeValue): string {
	if (!range.live) return range.to.toISOString();
	const minute = Math.floor(range.to.getTime() / 60_000) * 60_000;
	return new Date(minute).toISOString();
}

export function useFleetHistoryAll(range: FleetRangeValue) {
	const params = buildQuery(range);
	return useQuery<FleetHistoryAllResponse>({
		queryKey: [
			"workers",
			"history",
			range.kind,
			range.kind === "preset" ? range.preset : null,
			range.from.toISOString(),
			keyTo(range),
		],
		queryFn: async () => {
			const flat: Record<string, string> = { bucket: params.bucket };
			if (params.from) flat.from = params.from;
			if (params.to) flat.to = params.to;
			if (params.range) flat.range = params.range;
			const url = `/api/workers/history?${new URLSearchParams(flat).toString()}`;
			const res = await fetch(url, { credentials: "include" });
			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			const json = (await res.json()) as
				| FleetHistoryAllResponse
				| { success: false; error?: { message?: string } };
			if (!("trends" in json) || !json.success) {
				const msg =
					"error" in json && json.error?.message
						? json.error.message
						: "Failed to load history";
				throw new Error(msg);
			}
			return json;
		},
		refetchInterval: range.live ? FLEET_HISTORY_REFETCH_MS : false,
		placeholderData: keepPreviousData,
	});
}
